import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const TOAST_MS = 8000;

/** @type {{ id: number, quantity: number, selling_plan: string, properties: Record<string, string> } | null} */
let undoSnapshot = null;
let toastTimer = 0;
let busy = false;
let pairCodeRejected = false;

/**
 * @returns {string}
 */
function sectionIds() {
  const ids = new Set([SECTION_ID]);
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
    if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
  });
  return [...ids].join(',');
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} payload
 */
async function postCart(url, payload) {
  const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
  const json = await response.json();
  if (json.status && json.message) {
    throw new Error(json.description || json.message);
  }
  return json;
}

/**
 * @returns {Promise<any>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

/**
 * @param {any} json
 * @param {'add' | 'update' | 'remove'} action
 * @param {Array<Record<string, unknown>>} lines
 * @param {string} [source]
 */
function cartRoot() {
  const root = document.querySelector('[data-powdi-cart]');
  return root instanceof HTMLElement ? root : null;
}

function pairCode() {
  return (cartRoot()?.dataset.pairCode || '').trim();
}

function pairHandle() {
  return (cartRoot()?.dataset.pairHandle || 'acerola-vitaminas-c-milteliai').trim();
}

/**
 * @param {any} item
 */
function itemBlob(item) {
  return `${item?.handle || ''} ${item?.product_type || ''} ${item?.product_title || ''} ${item?.title || ''}`.toLowerCase();
}

/**
 * 5% acerola offer only while spirulina or chlorella is also in the cart.
 * @param {any} cart
 */
function needsPairDiscount(cart) {
  const items = cart?.items || [];
  const handle = pairHandle();
  const acerola = items.some((item) => item.handle === handle);
  const iron = items.some((item) => {
    const text = itemBlob(item);
    return item.handle !== handle && /spirulin|chlorell/.test(text);
  });
  return acerola && iron;
}

/**
 * @param {any} cart
 */
function discountCodes(cart) {
  return (cart?.discount_codes || []).map((entry) => entry?.code).filter(Boolean);
}

/**
 * Applies or removes the acerola pairing code without dropping other codes.
 * @param {any} cart
 */
async function applyPairState(cart) {
  const code = pairCode();
  if (!code || !cart || !Array.isArray(cart.items)) return cart;
  const current = discountCodes(cart);
  const has = current.some((entry) => entry.toUpperCase() === code.toUpperCase());
  const want = needsPairDiscount(cart);
  if (want === has) return cart;
  if (want && pairCodeRejected) return cart;

  const next = want
    ? [...current.filter((entry) => entry.toUpperCase() !== code.toUpperCase()), code]
    : current.filter((entry) => entry.toUpperCase() !== code.toUpperCase());
  const updated = await postCart(Theme.routes.cart_update_url, {
    discount: next.join(','),
    sections: sectionIds(),
    sections_url: window.location.pathname,
  });
  const rejected = (updated.discount_codes || []).some(
    (entry) => entry.code?.toUpperCase() === code.toUpperCase() && entry.applicable === false
  );
  if (!rejected) return updated;

  pairCodeRejected = true;
  const cleaned = current.filter((entry) => entry.toUpperCase() !== code.toUpperCase());
  return postCart(Theme.routes.cart_update_url, {
    discount: cleaned.join(','),
    sections: sectionIds(),
    sections_url: window.location.pathname,
  });
}

/**
 * @param {any} cart
 * @param {any} fallbackSections
 * @param {'add' | 'update' | 'remove'} action
 * @param {Array<Record<string, unknown>>} lines
 * @param {string} [source]
 */
async function publishCart(cart, fallbackSections, action, lines, source) {
  let next = cart;
  try {
    next = await applyPairState(cart);
  } catch (error) {
    console.error(error);
  }
  if (next && !next.sections && fallbackSections) next.sections = fallbackSections;
  dispatchCartUpdate(next, action, lines, source);
  return next;
}

function dispatchCartUpdate(json, action, lines, source) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
    detail: {
      sections: json.sections,
      items: json.items,
      itemCount: json.item_count,
      source: source || 'powdi-cart-drawer',
      didError: false,
    },
  });
}

/**
 * @param {HTMLElement} row
 */
function snapshotRow(row) {
  const sellingPlan = row.dataset.sellingPlan || '';
  return {
    id: Number(row.dataset.variantId),
    quantity: Number(row.dataset.qty || '1'),
    selling_plan: sellingPlan,
    properties: row.dataset.gift === 'true' ? { _powdi_gift: 'true' } : {},
  };
}

function showToast() {
  const toast = document.querySelector('[data-powdi-toast]');
  if (!(toast instanceof HTMLElement)) return;
  toast.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(hideToast, TOAST_MS);
}

function hideToast() {
  const toast = document.querySelector('[data-powdi-toast]');
  if (toast instanceof HTMLElement) toast.hidden = true;
  window.clearTimeout(toastTimer);
}

function restoreToastAfterMorph() {
  if (!undoSnapshot) return;
  window.setTimeout(() => {
    if (undoSnapshot) showToast();
  }, 160);
}

/**
 * @param {HTMLElement} row
 * @param {number} quantity
 * @param {boolean} [asRemove]
 */
async function changeQuantity(row, quantity, asRemove = false) {
  if (busy) return;
  busy = true;
  if (asRemove || quantity <= 0) undoSnapshot = snapshotRow(row);

  try {
    const json = await postCart(Theme.routes.cart_change_url, {
      id: row.dataset.key,
      quantity: Math.max(quantity, 0),
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });
    await publishCart(
      json,
      json.sections,
      quantity > 0 ? 'update' : 'remove',
      [{ id: row.dataset.key, quantity: Math.max(quantity, 0) }]
    );
    if (asRemove || quantity <= 0) restoreToastAfterMorph();
    else {
      undoSnapshot = null;
      hideToast();
    }
  } catch (error) {
    console.error(error);
    if (asRemove || quantity <= 0) undoSnapshot = null;
  } finally {
    busy = false;
  }
}

/**
 * @param {number} variantId
 * @param {number} quantity
 * @param {string} [sellingPlan]
 * @param {Record<string, string>} [properties]
 * @param {string} [source]
 */
async function addItem(variantId, quantity, sellingPlan = '', properties = {}, source) {
  /** @type {Record<string, unknown>} */
  const item = {
    id: variantId,
    quantity,
    properties,
  };
  if (sellingPlan) item.selling_plan = Number(sellingPlan);

  const json = await postCart(Theme.routes.cart_add_url, {
    items: [item],
    sections: sectionIds(),
    sections_url: window.location.pathname,
  });
  const cart = await fetchCart();
  cart.sections = json.sections;
  return publishCart(cart, json.sections, 'add', [{ merchandiseId: String(variantId), quantity }], source);
}

/**
 * @param {HTMLElement} row
 * @returns {{ id: number, plans: { id: number, name: string }[] }[]}
 */
function parseVariantPlans(row) {
  try {
    const parsed = JSON.parse(row.dataset.variantPlans || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

/**
 * @param {HTMLElement} row
 * @param {number | string} variantId
 * @returns {{ id: number, name: string }[]}
 */
function plansForVariant(row, variantId) {
  const entry = parseVariantPlans(row).find((item) => String(item.id) === String(variantId));
  return Array.isArray(entry?.plans) ? entry.plans : [];
}

/**
 * @param {string} [name]
 */
function planIntervalKey(name) {
  const text = String(name || '').toLowerCase();
  const match = text.match(/(\d+)\s*(week|savait|savaič|month|mėnes|menes|day|dien)/);
  if (match) {
    const unit = match[2];
    const letter = unit.startsWith('w') || unit.startsWith('s') ? 'w' : unit.startsWith('d') ? 'd' : 'm';
    return `${match[1]}-${letter}`;
  }
  if (/\bevery\s+week\b/.test(text) || text.includes('kiekvieną savaitę') || text.includes('kas savaitę')) return '1-w';
  if (/\bevery\s+month\b/.test(text) || text.includes('kiekvieną mėnesį') || text.includes('kas mėnesį')) return '1-m';
  if (/\bevery\s+day\b/.test(text) || text.includes('kiekvieną dieną') || text.includes('kas dieną')) return '1-d';
  return text.trim();
}

/**
 * Keep one-time as one-time. If the line is a subscription, keep the same
 * interval when the other pack has it; otherwise use that pack's first plan.
 * @param {HTMLElement} row
 * @param {number} nextVariantId
 */
function resolveSellingPlan(row, nextVariantId) {
  const currentId = row.dataset.sellingPlan || '';
  const nextPlans = plansForVariant(row, nextVariantId);
  const requiresPlan = row.dataset.requiresPlan === 'true';
  if (!nextPlans.length) return '';
  if (!currentId) return requiresPlan ? String(nextPlans[0].id) : '';
  if (nextPlans.some((plan) => String(plan.id) === String(currentId))) return currentId;

  const current = plansForVariant(row, row.dataset.variantId).find(
    (plan) => String(plan.id) === String(currentId)
  );
  const key = planIntervalKey(current?.name);
  const byInterval = key
    ? nextPlans.find((plan) => planIntervalKey(plan.name) === key)
    : null;
  return String((byInterval || nextPlans[0]).id);
}

/**
 * @param {HTMLElement} row
 * @param {number} variantId
 */
async function swapVariant(row, variantId) {
  if (busy || String(variantId) === row.dataset.variantId) return;
  busy = true;
  const quantity = Number(row.dataset.qty || '1');
  const sellingPlan = resolveSellingPlan(row, variantId);
  const oldKey = row.dataset.key;
  try {
    await postCart(Theme.routes.cart_change_url, {
      id: oldKey,
      quantity: 0,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });
    await addItem(variantId, quantity, sellingPlan);
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
  }
}

/**
 * @param {HTMLElement} row
 * @param {string} sellingPlan
 */
async function changeSellingPlan(row, sellingPlan) {
  if (busy) return;
  busy = true;
  try {
    const json = await postCart(Theme.routes.cart_change_url, {
      id: row.dataset.key,
      quantity: Number(row.dataset.qty || '1'),
      selling_plan: sellingPlan ? Number(sellingPlan) : 0,
      sections: sectionIds(),
      sections_url: window.location.pathname,
    });
    await publishCart(json, json.sections, 'update', [
      { id: row.dataset.key, quantity: Number(row.dataset.qty || '1') },
    ]);
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
  }
}

async function undoRemove() {
  if (!undoSnapshot || busy) return;
  const snapshot = undoSnapshot;
  undoSnapshot = null;
  hideToast();
  busy = true;
  try {
    await addItem(snapshot.id, snapshot.quantity, snapshot.selling_plan, snapshot.properties);
  } catch (error) {
    console.error(error);
  } finally {
    busy = false;
  }
}

/**
 * @param {Event} event
 */
function onClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const actionEl = target.closest('[data-powdi-action]');
  if (!(actionEl instanceof HTMLElement)) return;
  if (!actionEl.closest('[data-powdi-cart]')) return;

  const action = actionEl.dataset.powdiAction;
  if (action === 'undo') {
    event.preventDefault();
    undoRemove();
    return;
  }

  if (action === 'add-upsell') {
    event.preventDefault();
    const variantId = Number(actionEl.dataset.variantId || '');
    if (!variantId || busy) return;
    busy = true;
    addItem(variantId, 1)
      .catch((error) => console.error(error))
      .finally(() => {
        busy = false;
      });
    return;
  }

  const row = actionEl.closest('[data-key]');
  if (!(row instanceof HTMLElement) || row.dataset.gift === 'true') return;

  if (action === 'increase') {
    event.preventDefault();
    changeQuantity(row, Number(row.dataset.qty || '1') + 1);
  }

  if (action === 'decrease') {
    event.preventDefault();
    const next = Number(row.dataset.qty || '1') - 1;
    changeQuantity(row, next, next <= 0);
  }
}

/**
 * @param {Event} event
 */
function onChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLSelectElement)) return;
  if (!target.closest('[data-powdi-cart]')) return;

  const row = target.closest('[data-key]');
  if (!(row instanceof HTMLElement)) return;

  const action = target.dataset.powdiAction;
  if (action === 'variant') swapVariant(row, Number(target.value));
  if (action === 'selling-plan') changeSellingPlan(row, target.value);
}

document.addEventListener('click', onClick);
document.addEventListener('change', onChange);

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  if (form.dataset.pairReady === 'true') return;
  event.preventDefault();
  pairCodeRejected = false;
  fetchCart()
    .then((cart) => applyPairState(cart))
    .catch((error) => console.error(error))
    .finally(() => {
      form.dataset.pairReady = 'true';
      if (!form.querySelector('input[name="checkout"]')) {
        const checkout = document.createElement('input');
        checkout.type = 'hidden';
        checkout.name = 'checkout';
        form.append(checkout);
      }
      form.submit();
    });
});
